import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faRocket, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

function WhatsAppIntegration() {
  const [qrCode, setQrCode] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [profileName, setProfileName] = useState('');
  const [error, setError] = useState('');
  const [sessionName, setSessionName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showInstanceInfo, setShowInstanceInfo] = useState(false);

  const shortToken = useMemo(
    () => sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken') || '',
    []
  );

  const authHeaders = useMemo(() => {
    if (!shortToken) {
      return {};
    }

    return {
      Authorization: `Bearer ${shortToken}`,
    };
  }, [shortToken]);

  useEffect(() => {
    if (!shortToken) {
      setError('No se encontró el token de autenticación');
      return;
    }

    const normalizedToken = shortToken.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 50);
    const nextSessionName = `CitasPro_${normalizedToken}`;
    setSessionName(nextSessionName);
    initializeSession(nextSessionName);
  }, [shortToken]);

  useEffect(() => {
    if (!sessionName || connectionStatus === 'WORKING') {
      return;
    }

    const intervalId = setInterval(() => {
      refreshStatus(sessionName);
      refreshQr(sessionName);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [sessionName, connectionStatus]);

  const initializeSession = async (targetSessionName) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await axios.post(
        '/api/whatsapp/session',
        { sessionName: targetSessionName },
        { headers: authHeaders }
      );

      const status = response.data?.status;
      updateStatusData(status);

      if ((status?.status || '').toUpperCase() !== 'WORKING') {
        await refreshQr(targetSessionName);
      }
    } catch (requestError) {
      console.error('Error al inicializar sesión WAHA:', requestError);
      setError('No se pudo inicializar la sesión de WhatsApp');
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatusData = (statusData) => {
    const currentStatus = (statusData?.status || '').toUpperCase();
    setConnectionStatus(currentStatus);

    if (currentStatus === 'WORKING' && statusData?.me) {
      setPhoneNumber((statusData.me.id || '').split('@')[0]);
      setProfileName(statusData.me.pushName || '');
      setQrCode('');
      return;
    }

    if (currentStatus !== 'WORKING') {
      setPhoneNumber('');
      setProfileName('');
    }
  };

  const refreshStatus = async (targetSessionName) => {
    if (!targetSessionName) {
      return;
    }

    try {
      const response = await axios.get(`/api/whatsapp/session/${targetSessionName}/status`, {
        headers: authHeaders,
      });
      updateStatusData(response.data?.status);
    } catch (requestError) {
      console.error('Error al consultar estado WAHA:', requestError);
    }
  };

  const refreshQr = async (targetSessionName) => {
    if (!targetSessionName) {
      return;
    }

    try {
      const response = await axios.get(`/api/whatsapp/session/${targetSessionName}/qr`, {
        headers: authHeaders,
      });

      if (response.data?.qrCode) {
        setQrCode(response.data.qrCode);
      }
    } catch (requestError) {
      if (requestError?.response?.status !== 404) {
        console.error('Error al refrescar QR WAHA:', requestError);
      }
    }
  };

  const handleCheckStatus = () => {
    if (!sessionName) {
      setError('No se encontró una sesión activa');
      return;
    }

    refreshStatus(sessionName);
  };

  const handleRestart = async () => {
    if (!sessionName) {
      setError('No se encontró una sesión activa');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`/api/whatsapp/session/${sessionName}/restart`, {}, { headers: authHeaders });
      await refreshStatus(sessionName);
      await refreshQr(sessionName);
    } catch (requestError) {
      console.error('Error al reiniciar sesión WAHA:', requestError);
      setError('No se pudo reiniciar la sesión de WhatsApp');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!sessionName) {
      setError('No se encontró una sesión activa');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await axios.post(`/api/whatsapp/session/${sessionName}/logout`, {}, { headers: authHeaders });
      setConnectionStatus('');
      setPhoneNumber('');
      setProfileName('');
      setQrCode('');
      await initializeSession(sessionName);
    } catch (requestError) {
      console.error('Error al desconectar sesión WAHA:', requestError);
      setError('No se pudo cerrar la sesión de WhatsApp');
    } finally {
      setIsLoading(false);
    }
  };

  const isConnected = connectionStatus === 'WORKING';

  return (
    <div className="whatsapp-integration bg-white shadow-lg rounded-lg p-6 max-w-xl mx-auto mt-8">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Integración con WhatsApp (WAHA)</h2>

      {error && (
        <div className="error-message mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="connection-info">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2 text-gray-700">Estado de conexión</h3>
            <p className={`font-medium ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
              {isConnected ? 'Conectado' : connectionStatus || 'Desconectado'}
            </p>
          </div>

          {isConnected && (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2 text-gray-700">Número de teléfono</h3>
                <p className="font-medium">{phoneNumber || 'No disponible'}</p>
              </div>

              {profileName && (
                <div className="mb-4">
                  <h3 className="text-lg font-semibold mb-2 text-gray-700">Nombre del perfil</h3>
                  <p className="font-medium">{profileName}</p>
                </div>
              )}

              <div className="bg-green-100 p-3 rounded-lg text-center max-w-[160px]">
                <FontAwesomeIcon icon={faComments} className="text-2xl text-green-500 mb-2" />
                <p className="font-medium text-gray-800">Sesión activa</p>
                <p className="text-sm text-gray-600">WAHA WORKING</p>
              </div>
            </>
          )}
        </div>

        <div className="qr-code-or-profile">
          {isConnected ? (
            <div className="text-center">
              <p className="mt-6 text-green-700 font-medium">WhatsApp conectado correctamente.</p>
            </div>
          ) : qrCode ? (
            <div className="qr-code-container text-center">
              <h3 className="text-lg font-semibold mb-2 text-gray-700">Código QR</h3>
              <img src={qrCode} alt="QR Code" className="mx-auto border-2 border-gray-300 rounded" />
              <p className="mt-2 text-sm text-gray-600">Escanea este código con tu WhatsApp</p>
            </div>
          ) : (
            <p className="text-center text-gray-600">{isLoading ? 'Cargando...' : 'QR no disponible aún'}</p>
          )}
        </div>
      </div>

      <div className="flex justify-center mt-6 space-x-4 flex-wrap gap-y-3">
        {isConnected ? (
          <button
            onClick={handleDisconnect}
            disabled={isLoading}
            className="px-6 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition duration-300 shadow-md disabled:opacity-50"
          >
            Desconectar
          </button>
        ) : (
          <button
            onClick={handleCheckStatus}
            disabled={isLoading}
            className="px-6 py-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition duration-300 shadow-md flex items-center space-x-2 group disabled:opacity-50"
          >
            <span>Verificar estado</span>
            <FontAwesomeIcon
              icon={faRocket}
              className="transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300"
            />
          </button>
        )}

        {!isConnected && (
          <button
            onClick={handleRestart}
            disabled={isLoading}
            className="px-6 py-2 bg-amber-500 text-white rounded-full hover:bg-amber-600 transition duration-300 shadow-md disabled:opacity-50"
          >
            Regenerar QR
          </button>
        )}

        <button
          onClick={() => setShowInstanceInfo(!showInstanceInfo)}
          className="px-6 py-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition duration-300 shadow-md flex items-center space-x-2"
        >
          <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
          <span>Información de la sesión</span>
        </button>
      </div>

      {showInstanceInfo && (
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Información de la sesión:</h3>
          <p className="mb-2"><strong>Nombre de la sesión:</strong></p>
          <p className="bg-white p-2 rounded border border-gray-300 break-all mb-4">{sessionName || 'No disponible'}</p>
          <p className="mb-2"><strong>Estado WAHA:</strong></p>
          <p className="bg-white p-2 rounded border border-gray-300 break-all mb-4">{connectionStatus || 'Desconectado'}</p>
          <p className="text-sm text-gray-600 mt-4">
            Esta integración usa WAHA como proveedor de WhatsApp, sin dependencia de Evolution API.
          </p>
        </div>
      )}
    </div>
  );
}

export default WhatsAppIntegration;
